/**
 * Theme-Knobs model for the Inline-Theme-Editor (Spec 340).
 *
 * The editor exposes a tiny, fixed set of knobs that drive the CTA-button look of
 * a two-zone-v2 payment-page stylesheet (fill colour, optional gradient, glow,
 * border-radius). These values are the single source of truth: they are persisted
 * as JSON inside a `@theme-knobs` CSS comment header (see `theme-knobs-header.ts`)
 * and rendered into CSS by the template generator (`generate-stylesheet.ts`).
 *
 * Pure module — no DOM, no network. Fully unit-testable.
 */

/** Knob set, version 1. */
export interface ThemeKnobs {
  /** Schema version of the knob set (currently always 1). */
  v: 1;
  /** Primary CTA fill colour, canonical `#RRGGBB`. */
  ctaColor: string;
  /** Second gradient stop (`#RRGGBB`) when the gradient switch is on, else null. */
  ctaGradientTo: string | null;
  /** Whether the glow ring around the CTA button is on. Colour is derived from `ctaColor`. */
  glow: boolean;
  /** CTA border-radius in px (0…999; 999 == pill). */
  radius: number;
}

export const MIN_RADIUS = 0;
export const MAX_RADIUS = 999;

/**
 * Default knobs for a fresh theme. Calibrated to the real two-zone-v2 CTA button
 * (`.tz-cta-btn`): cyan primary, glow on, 16 px radius.
 */
export const DEFAULT_KNOBS: ThemeKnobs = {
  v: 1,
  ctaColor: '#1FA3EE',
  ctaGradientTo: null,
  glow: true,
  radius: 16,
};

/**
 * Normalise a colour string to canonical `#RRGGBB` (uppercase). Accepts `#RGB`,
 * `#RRGGBB`, with or without leading `#`, any case. Returns null for anything that
 * is not a valid hex colour — callers reject rather than interpolate raw input into
 * CSS (defence against CSS injection via a crafted knobs header).
 */
export function normalizeHex(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  let s = input.trim();
  if (s.startsWith('#')) s = s.slice(1);
  if (/^[0-9a-fA-F]{3}$/.test(s)) {
    s = s
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return null;
  return `#${s.toUpperCase()}`;
}

/** Clamp a radius to the [0, 999] range and round to an integer. Non-finite → default. */
export function clampRadius(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_KNOBS.radius;
  return Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Math.round(value)));
}

/**
 * Coerce an untrusted, parsed object into valid `ThemeKnobs`, or null if it cannot
 * be made valid. Used when reading the `@theme-knobs` header back from CSS: any
 * malformed/invalid value yields null so the caller falls back to {@link DEFAULT_KNOBS}.
 */
export function coerceKnobs(raw: unknown): ThemeKnobs | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;

  // Version must be exactly 1. A missing or unknown version (e.g. a future v2 header)
  // is rejected rather than silently read as v1 — forward-compatibility + defensive parse.
  if (o.v !== 1) return null;

  const ctaColor = normalizeHex(o.ctaColor);
  if (!ctaColor) return null;

  let ctaGradientTo: string | null = null;
  if (o.ctaGradientTo !== undefined && o.ctaGradientTo !== null) {
    ctaGradientTo = normalizeHex(o.ctaGradientTo);
    if (!ctaGradientTo) return null;
  }

  if (o.glow !== undefined && typeof o.glow !== 'boolean') return null;
  const glow = typeof o.glow === 'boolean' ? o.glow : DEFAULT_KNOBS.glow;

  if (o.radius !== undefined && (typeof o.radius !== 'number' || !Number.isFinite(o.radius))) {
    return null;
  }
  const radius = clampRadius(o.radius ?? DEFAULT_KNOBS.radius);

  return { v: 1, ctaColor, ctaGradientTo, glow, radius };
}
